<script lang="ts">
  /**
   * Ops — is this estate actually configured the way we think it is.
   *
   * Four panels, each loading and failing on its own, following `Estate.svelte`'s
   * pattern: one disabled API must not blank an admin page.
   *
   *   Drift      item 3 — the assertions, with the exact command that fixes each
   *   Readiness  item 2 — one row per repo: WIF, secrets, workflow, last run
   *   Runbook    item 6 — the README's manual blocks, as guarded buttons
   *   Previews   item 5 — live Hosting preview channels
   *
   * Status is glyph + label + colour, never colour alone: blue for passing, amber
   * for failing, grey for not checked. There is no green anywhere in this
   * programme, deliberately.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { stepUp } from "../lib/stepup.svelte";

  type DriftRow = {
    id: string;
    title: string;
    severity: "info" | "attention" | "urgent";
    status: "pass" | "fail" | "skip" | "error";
    detail: string;
    fix: string | null;
    at: string | null;
  };
  type Repo = {
    repo: string;
    reachable: boolean;
    hasWorkflow: boolean;
    hasWifSecrets: boolean;
    neverRun: boolean;
    secretNames: string[];
    variableNames: string[];
    lastRun: { status: string; conclusion: string | null; at: string | null; by: string | null; url: string | null; sha: string | null } | null;
  };
  type Preview = { id: string; repo: string; pr: number | null; title: string; url: string; expiresAt: string | null; expiresInMs: number };
  type Action = { id: string; label: string };
  type SecretRow = {
    project: string;
    name: string;
    versions: number;
    enabledVersions: number;
    ageDays: number | null;
    noEnabledVersion: boolean;
    stale: boolean;
  };
  type Clash = { key: string; spellings: string[]; projects: { project: string; name: string }[]; suggested: string };

  type Panel<T> = { data: T | null; error: string | null; loading: boolean };

  // Declared individually rather than via a factory: `$state` is a compiler rune,
  // not a function, and may only appear as a variable or class field initialiser.
  const drift = $state<Panel<{ results: DriftRow[]; everRun: boolean }>>({ data: null, error: null, loading: true });
  const readiness = $state<Panel<{ repos: Repo[]; reachable: boolean; wif: Record<string, boolean>; note: string | null }>>({
    data: null, error: null, loading: true,
  });
  const previews = $state<Panel<{ previews: Preview[] }>>({ data: null, error: null, loading: true });
  const runbook = $state<Panel<{ actions: Action[] }>>({ data: null, error: null, loading: true });
  const secrets = $state<
    Panel<{
      projects: { project: string; readable: boolean; count: number }[];
      secrets: SecretRow[];
      inconsistent: Clash[];
      stale: SecretRow[];
      unreadable: string[];
      note: string | null;
    }>
  >({ data: null, error: null, loading: true });

  let busy = $state<string | null>(null);
  let toast = $state<{ kind: "ok" | "warn"; text: string } | null>(null);
  let open = $state<string | null>(null);
  /** Typed confirmation for runbook buttons — item 6 asks for one explicitly. */
  let confirming = $state<{ id: string; label: string; typed: string } | null>(null);

  async function fill<T>(p: Panel<T>, path: string) {
    p.loading = true;
    try {
      p.data = await api.get<T>(path);
      p.error = null;
    } catch (e) {
      p.error = e instanceof ApiError ? e.message : "Unavailable";
    } finally {
      p.loading = false;
    }
  }

  function loadAll() {
    void fill(drift, "/ops/drift");
    void fill(readiness, "/admin/readiness");
    void fill(previews, "/admin/previews");
    void fill(runbook, "/ops/runbook");
    void fill(secrets, "/admin/secrets");
  }

  $effect(() => loadAll());

  const failing = $derived((drift.data?.results ?? []).filter((r) => r.status === "fail"));
  const unchecked = $derived((drift.data?.results ?? []).filter((r) => r.status === "skip" || r.status === "error"));

  async function sweepNow() {
    busy = "sweep";
    try {
      const res = await api.post<{ checked: number; results: DriftRow[] }>("/ops/drift-sweep/run");
      const bad = res.results.filter((r) => r.status === "fail").length;
      toast = {
        kind: bad === 0 ? "ok" : "warn",
        text: bad === 0 ? `All ${res.checked} assertions pass.` : `${bad} of ${res.checked} assertions failing.`,
      };
      await fill(drift, "/ops/drift");
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not run the sweep" };
    } finally {
      busy = null;
    }
  }

  async function runAction() {
    if (!confirming) return;
    const { id, label } = confirming;
    confirming = null;
    busy = id;
    try {
      const ok = await stepUp.guard(async () => {
        const res = await api.post<{ summary: string }>(`/ops/runbook/${id}`);
        toast = { kind: "ok", text: res.summary };
      }, label);
      if (!ok) toast = { kind: "warn", text: "Cancelled — nothing ran." };
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not run that" };
    } finally {
      busy = null;
    }
  }

  function days(ms: number): string {
    const d = Math.floor(ms / 86_400_000);
    if (d >= 1) return `${d}d left`;
    const h = Math.max(0, Math.floor(ms / 3_600_000));
    return `${h}h left`;
  }
</script>

<div class="wrap">
  {#if toast}
    <div class="attn" class:attn--warn={toast.kind === "warn"} role="status">
      <span class="attn__badge" aria-hidden="true"><Icon name={toast.kind === "warn" ? "alert" : "check"} size={12} /></span>
      <span class="attn__text">{toast.text}</span>
    </div>
  {/if}

  <!-- ── Drift ─────────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Config drift</span>
      <span class="acts">
        {#if drift.data}
          <span class="sub">
            {failing.length === 0 ? "all passing" : `${failing.length} failing`}{unchecked.length > 0 ? ` · ${unchecked.length} not checked` : ""}
          </span>
        {/if}
        <button class="btn-primary" onclick={sweepNow} disabled={busy === "sweep"}>
          {busy === "sweep" ? "Sweeping…" : "Sweep now"}
        </button>
      </span>
    </div>

    {#if drift.loading}
      <p class="sub pad">Loading…</p>
    {:else if drift.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {drift.error}</p>
    {:else if drift.data && !drift.data.everRun}
      <p class="sub pad">
        <Icon name="pause" size={12} /> The sweep has never run. Nothing here is failing — nothing here has been
        checked. Press <strong>Sweep now</strong>, or create the 04:30 scheduler job.
      </p>
    {:else if drift.data}
      {#each drift.data.results as r (r.id)}
        <div class="rule" class:rule--fail={r.status === "fail"}>
          <button class="rule__head" onclick={() => (open = open === r.id ? null : r.id)} aria-expanded={open === r.id}>
            {#if r.status === "pass"}
              <span class="status status--ok"><Icon name="check" size={12} /> passing</span>
            {:else if r.status === "fail"}
              <span class="status status--warn"><Icon name="alert" size={12} /> failing</span>
            {:else if r.status === "error"}
              <span class="status status--warn"><Icon name="alert" size={12} /> could not check</span>
            {:else}
              <span class="status status--idle"><Icon name="pause" size={12} /> not checked</span>
            {/if}
            <span class="rule__id num">{r.id}</span>
            <span class="rule__t">{r.title}</span>
            <span class="sub num">{r.at ? dateZA(r.at) : "—"}</span>
            <Icon name={open === r.id ? "chevron-down" : "chevron-right"} size={14} />
          </button>
          {#if open === r.id}
            <div class="rule__body">
              <p class="sub">{r.detail}</p>
              {#if r.fix}
                <!-- A drift alert with no remedy becomes noise within a week. -->
                <div class="fixhead sub">Fix</div>
                <pre class="fix">{r.fix}</pre>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    {/if}
  </section>

  <!-- ── Deploy readiness ──────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Deploy readiness</span>
      <button class="lnk" onclick={() => fill(readiness, "/admin/readiness")}>Refresh</button>
    </div>
    {#if readiness.loading}
      <p class="sub pad">Loading…</p>
    {:else if readiness.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {readiness.error}</p>
    {:else if readiness.data && !readiness.data.reachable}
      <p class="sub pad"><Icon name="pause" size={12} /> {readiness.data.note ?? "GitHub not reachable."}</p>
    {:else if readiness.data}
      <div class="scroll">
        <table>
          <thead>
            <tr><th>Repo</th><th>WIF secrets</th><th>Workflow</th><th>Last run</th><th>Secrets · vars</th></tr>
          </thead>
          <tbody>
            {#each readiness.data.repos as r (r.repo)}
              <tr>
                <td class="nm">{r.repo}</td>
                <td>
                  {#if r.hasWifSecrets}
                    <span class="status status--ok"><Icon name="check" size={12} /> set</span>
                  {:else}
                    <span class="status status--warn"><Icon name="alert" size={12} /> missing</span>
                  {/if}
                </td>
                <td>
                  {#if r.hasWorkflow}
                    <span class="status status--ok"><Icon name="check" size={12} /> present</span>
                  {:else}
                    <span class="status status--warn"><Icon name="alert" size={12} /> none</span>
                  {/if}
                </td>
                <td>
                  {#if r.neverRun}
                    <!-- A repo with a workflow that has never run is a repo that
                         gets deployed from a laptop. -->
                    <span class="status status--warn"><Icon name="alert" size={12} /> never run</span>
                  {:else if r.lastRun}
                    <span class="sub">
                      {#if r.lastRun.url}
                        <a href={r.lastRun.url} target="_blank" rel="noreferrer">{r.lastRun.conclusion ?? r.lastRun.status}</a>
                      {:else}
                        {r.lastRun.conclusion ?? r.lastRun.status}
                      {/if}
                      · <span class="num">{r.lastRun.at ? dateZA(r.lastRun.at) : "—"}</span>
                      {#if r.lastRun.sha}· <span class="num">{r.lastRun.sha}</span>{/if}
                    </span>
                  {:else}
                    <span class="sub">—</span>
                  {/if}
                </td>
                <td class="sub num">{r.secretNames.length} · {r.variableNames.length}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      {#if readiness.data.note}<p class="sub foot"><Icon name="alert" size={12} /> {readiness.data.note}</p>{/if}
    {/if}
  </section>

  <!-- ── Runbook ───────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">Runbook</span><span class="sub">owner only · confirms twice</span></div>
    {#if runbook.loading}
      <p class="sub pad">Loading…</p>
    {:else if runbook.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {runbook.error}</p>
    {:else if runbook.data}
      <div class="rb">
        {#each runbook.data.actions as a (a.id)}
          <button class="rb__btn" disabled={busy === a.id} onclick={() => (confirming = { id: a.id, label: a.label, typed: "" })}>
            <Icon name="wrench" size={14} />
            <span>{busy === a.id ? "Running…" : a.label}</span>
          </button>
        {/each}
      </div>
      <p class="sub foot">
        Anything genuinely destructive — deleting a project, tearing down infrastructure — stays a deliberate
        act at the console and deliberately has no button here.
      </p>
    {/if}
  </section>

  <!-- ── Secrets ───────────────────────────────────────────────────────── -->
  <!-- Names, ages and version counts only. This panel cannot read a secret's
       value and does not have the permission to: it uses secrets.list and
       versions.list, which need roles/secretmanager.viewer rather than the
       secretAccessor a value read would require. Comparing actual bytes lives in
       `npm run sync-secret`, run by a person with their own credentials. -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Credentials</span>
      <span class="sub">
        {#if secrets.data}
          {secrets.data.secrets.length} across {secrets.data.projects.filter((p) => p.readable).length} projects · names only
        {/if}
      </span>
    </div>

    {#if secrets.loading}
      <p class="sub pad">Loading…</p>
    {:else if secrets.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {secrets.error}</p>
    {:else if secrets.data}
      {#if secrets.data.inconsistent.length > 0}
        <!-- The whole reason this panel exists. One credential under several
             spellings survives happily until a rotation, and then one stale copy
             fails days later in a service nobody was touching. -->
        {#each secrets.data.inconsistent as c (c.key)}
          <div class="row row--warn">
            <Icon name="alert" size={14} />
            <div class="grow">
              <div class="row__t">Same credential, {c.spellings.length} different names</div>
              <div class="sub">
                {#each c.projects as p (p.project + p.name)}
                  <span class="num">{p.project}</span>/<strong>{p.name}</strong>{" "}
                {/each}
              </div>
              <div class="sub">
                Settle on <code>{c.suggested}</code> — <code>npm run sync-secret -- {c.suggested} --from steyn-fabric --apply</code>
              </div>
            </div>
          </div>
        {/each}
      {/if}

      {#if secrets.data.stale.length > 0}
        {#each secrets.data.stale as s (s.project + s.name)}
          <div class="row row--warn">
            <Icon name={s.noEnabledVersion ? "alert" : "clock"} size={14} />
            <div class="grow">
              <div class="row__t">{s.name} <span class="sub num">{s.project}</span></div>
              <div class="sub">
                {#if s.noEnabledVersion}
                  No enabled version — anything reading this gets nothing.
                {:else}
                  Newest enabled version is {s.ageDays} days old. Rotate it.
                {/if}
              </div>
            </div>
          </div>
        {/each}
      {/if}

      {#if secrets.data.inconsistent.length === 0 && secrets.data.stale.length === 0}
        <p class="sub pad">
          <Icon name="check" size={12} /> Every credential has one name and a version younger than a year.
        </p>
      {/if}

      <div class="scroll">
        <table>
          <thead><tr><th>Secret</th><th>Project</th><th>Versions</th><th>Newest</th></tr></thead>
          <tbody>
            {#each secrets.data.secrets as s (s.project + s.name)}
              <tr>
                <td class="nm">{s.name}</td>
                <td class="sub num">{s.project}</td>
                <td class="sub num">{s.enabledVersions} of {s.versions}</td>
                <td class="sub num">{s.ageDays === null ? "—" : `${s.ageDays}d`}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      {#if secrets.data.note}
        <p class="sub foot"><Icon name="pause" size={12} /> {secrets.data.note}</p>
      {/if}
      <p class="sub foot">
        <Icon name="shield" size={12} />
        Values are never shown here and cannot be — this screen holds list permission only.
      </p>
    {/if}
  </section>

  <!-- ── Previews ──────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">Live previews</span><span class="sub">7-day channels</span></div>
    {#if previews.loading}
      <p class="sub pad">Loading…</p>
    {:else if previews.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {previews.error}</p>
    {:else if (previews.data?.previews ?? []).length === 0}
      <p class="sub pad">
        <Icon name="pause" size={12} /> No preview channels. CI posts them here when a pull request is opened.
      </p>
    {:else}
      {#each previews.data!.previews as p (p.id)}
        <div class="row">
          <Icon name="external" size={14} />
          <div class="grow">
            <div class="row__t">{p.title || `${p.repo} #${p.pr ?? "?"}`}</div>
            <div class="sub"><a href={p.url} target="_blank" rel="noreferrer">{p.url}</a></div>
          </div>
          <span class="sub num">{days(p.expiresInMs)}</span>
        </div>
      {/each}
    {/if}
  </section>
</div>

<!-- Typed confirmation. The word is the action's own id, so muscle memory cannot
     carry you through a different button than the one you meant. -->
{#if confirming}
  <div class="scrim" role="dialog" aria-modal="true" aria-labelledby="rb-title">
    <div class="card--hero box">
      <h2 id="rb-title">{confirming.label}</h2>
      <p class="sub">
        Type <code>{confirming.id}</code> to confirm. This runs immediately against the live estate and is
        recorded in the trail.
      </p>
      <input bind:value={confirming.typed} placeholder={confirming.id} aria-label="Type the action id to confirm" />
      <div class="boxacts">
        <button class="lnk" onclick={() => (confirming = null)}>Cancel</button>
        <button class="btn-primary" disabled={confirming.typed.trim() !== confirming.id} onclick={runAction}>Run it</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card { padding: 13px 15px 15px; }
  .sec { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding-bottom: 9px; }
  .acts { display: flex; align-items: center; gap: 10px; }
  .pad { padding: 6px 0 2px; display: flex; align-items: center; gap: 6px; }
  .foot { margin: 9px 0 0; display: flex; align-items: flex-start; gap: 6px; }
  .scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.7812rem; }
  th { text-align: left; font-size: 0.72rem; font-weight: 700; color: var(--mut); padding: 6px 8px;
       border-bottom: 1px solid var(--line); white-space: nowrap; }
  td { padding: 9px 8px; border-bottom: 1px solid var(--line); vertical-align: middle; }
  tbody tr:last-child td { border-bottom: none; }
  .nm { font-weight: 700; color: var(--tx); }
  .lnk { font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }
  button:disabled { opacity: 0.45; cursor: default; }

  .rule { border-bottom: 1px solid var(--line); }
  .rule:last-child { border-bottom: none; }
  .rule--fail { border-left: 2px solid var(--warn); padding-left: 8px; margin-left: -10px; }
  .rule__head { display: flex; align-items: center; gap: 10px; width: 100%; padding: 9px 2px; text-align: left; }
  .rule__id { font-size: 0.72rem; font-weight: 800; color: var(--mut); min-width: 26px; }
  .rule__t { flex: 1; font-size: 0.7812rem; font-weight: 700; color: var(--tx); }
  .rule__body { padding: 0 2px 12px 2px; }
  .fixhead { font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.4px; margin: 8px 0 4px; }
  /* Commands scroll inside their own box; the page body never scrolls sideways. */
  .fix { margin: 0; padding: 9px 11px; overflow-x: auto; border-radius: var(--r-control);
         background: var(--fill); box-shadow: inset 0 0 0 1px var(--line);
         font-size: 0.72rem; line-height: 1.55; white-space: pre; color: var(--tx2); }
  code { font-size: 0.72rem; color: var(--acc); overflow-wrap: anywhere; }

  .rb { display: flex; flex-wrap: wrap; gap: 8px; }
  .rb__btn { display: inline-flex; align-items: center; gap: 7px; padding: 8px 12px;
             border-radius: var(--r-control); box-shadow: inset 0 0 0 1px var(--line);
             font-size: 0.75rem; font-weight: 700; color: var(--tx2); }
  .rb__btn:hover:not(:disabled) { color: var(--tx); background: var(--card-hover); }

  .row { display: flex; gap: 9px; padding: 9px 10px; border-radius: var(--r-control);
         box-shadow: inset 0 0 0 1px var(--line); margin-bottom: 6px; align-items: center; }
  .row__t { font-size: 0.7812rem; color: var(--tx); font-weight: 700; }
  .grow { flex: 1; min-width: 0; }
  .grow .sub { overflow-wrap: anywhere; }

  .scrim { position: fixed; inset: 0; z-index: 80; display: grid; place-items: center; padding: 24px;
           background: rgb(0 0 0 / 0.5); }
  .box { width: min(420px, 100%); padding: 20px; }
  .box h2 { margin: 0 0 6px; font-size: 0.9375rem; font-weight: 800; color: var(--tx); }
  .box input { width: 100%; margin-top: 12px; background: var(--fill); color: var(--tx);
               border: 1px solid var(--line); border-radius: var(--r-control); padding: 7px 9px; font-size: 0.7812rem; }
  .box code { font-size: 0.72rem; color: var(--acc); }
  .boxacts { display: flex; align-items: center; justify-content: flex-end; gap: 14px; margin-top: 14px; }
</style>
